import { inject, Service } from '@angular/core';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { PageDataType } from '@common/paged-data/enums/page-data-type.enum';
import { PageFilterGroupType } from '@common/paged-data/enums/page-filter-group-type.enum';
import { PageFilterType } from '@common/paged-data/enums/page-filter-type.enum';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { ReviewFileStatus } from '@common/reviews/enums/review-file-status.enum';
import { ReviewFile } from '@common/reviews/models/review-file.model';
import { CollectionServiceBase } from '@portal-core/data/collection/services/collection.service.base';
import { ReviewFileGridItem } from '@portal-core/reviews/review-files/models/review-file-grid-item.model';
import { ReviewFilesApiService } from '@portal-core/reviews/review-files/services/review-files-api.service';
import { ReviewFilesDataService } from '@portal-core/reviews/review-files/services/review-files-data.service';
import { Resettable } from '@portal-core/util/resettable.decorator';
import { map, Observable, of, switchMap, tap } from 'rxjs';

@Service()
@Resettable()
export class ReviewFilesService extends CollectionServiceBase<ReviewFile> {
  private readonly reviewFilesApiService = inject(ReviewFilesApiService);

  constructor() {
    super(inject(ReviewFilesDataService));
  }

  protected override fetchItemById$(reviewFileId: number): Observable<ReviewFile> {
    return this.reviewFilesApiService.getReviewFileById$(reviewFileId);
  }

  protected override fetchItemsById$(reviewFileIds: number[]): Observable<ReviewFile[]> {
    return this.reviewFilesApiService.getReviewFilesByIds$(reviewFileIds);
  }

  getReviewFilesPage$(licenseId: number, reviewersListLimit: number, filter?: PageFilter): Observable<Page<ReviewFileGridItem>> {
    return this.reviewFilesApiService.getReviewFilesPage$(licenseId, reviewersListLimit, filter);
  }

  getReviewFilesPageByPackageId$(reviewPackageId: number, filters: PageFilter): Observable<Page<ReviewFileGridItem>> {
    return this.reviewFilesApiService.getReviewFilesPageByPackageId$(reviewPackageId, filters);
  }

  getReviewFileByPackageIdAndFilePath$(licenseId: number, reviewPackageId: number, filePath: string): Observable<ReviewFile> {
    const filter: PageFilter = {
      Id: 'review-file-by-package-id-and-file-path',
      Type: PageFilterGroupType.Custom,
      PerPage: 1,
      Filters: [
        {
          FilterType: PageFilterType.Equals,
          PropertyName: "ReviewPackageId",
          PropertyType: PageDataType.Int,
          PropertyValue: reviewPackageId
        },
        {
          FilterType: PageFilterType.Equals,
          PropertyName: "Path",
          PropertyType: PageDataType.String,
          PropertyValue: filePath
        }
      ]
    };

    return this.getReviewFilesPage$(licenseId, 0, filter).pipe(
      switchMap(page => of(page.Items?.[0]))
    );
  }

  updateReviewFileStatus$(reviewFileId: number, reviewFileStatus: ReviewFileStatus, resetReviewerStatus: boolean): Observable<MadCloudResult<void>> {
    return this.reviewFilesApiService.updateReviewFileStatus$(reviewFileId, reviewFileStatus, resetReviewerStatus).pipe(
      // Wait for the collection update to finish, then surface the endpoint's result to the caller.
      switchMap(result => this.updateItems$({
        [reviewFileId]: {
          Status: reviewFileStatus
        }
      }).pipe(
        map(() => result)
      ))
    );
  }

  bulkUpdateReviewFileStatus$(reviewIds: number[], status: ReviewFileStatus, resetReviewerStatus: boolean): Observable<void> {
    // This endpoint replies with an empty body, so there is nothing to carry across the collection
    // update - unlike updateReviewFileStatus$, whose endpoint does return a MadCloudResult.
    return this.reviewFilesApiService.bulkUpdateReviewFileStatus$(reviewIds, status, resetReviewerStatus).pipe(
      switchMap(() => this.updateItems$(reviewIds.map(reviewId => {
        return {
          Id: reviewId,
          Status: status
        };
      })))
    );
  }

  addReviewFiles$(reviewPackageId: number, paths: string[]): Observable<MadCloudResult<ReviewFile>> {
    return this.reviewFilesApiService.addReviewFiles$(reviewPackageId, paths);
  }

  deleteReviewFile$(reviewPackageId: number, reviewFileId: number): Observable<void> {
    return this.reviewFilesApiService.deleteReviewFile$(reviewPackageId, reviewFileId).pipe(
      tap(() => this.deleteItems$([reviewFileId]))
    );
  }

  /** Returns the display text for a review file status. */
  getReviewFileStatusText(status: ReviewFileStatus | null | undefined): string {
    switch (status) {
      case ReviewFileStatus.Open:
        return 'Open';
      case ReviewFileStatus.Closed:
        return 'Closed';
      case ReviewFileStatus.Accepted:
        return 'Accepted';
    }
  }

  /** Returns the theme color name for a review file status, optionally at a luminosity variant. */
  getReviewFileStatusColor(status: ReviewFileStatus | null | undefined, luminosity: '' | 'muted' | 'vivid' = ''): string {
    switch (status) {
      case ReviewFileStatus.Open:
        return `caution${luminosity ? '-' + luminosity : ''}`;
      case ReviewFileStatus.Closed:
      case ReviewFileStatus.Accepted:
        return `info${luminosity ? '-' + luminosity : ''}`;
    }
  }
}
